/**
 * API types, hand-written rather than generated.
 *
 * `api/openapi.json` is gitignored and only exists after `npm run openapi:json`,
 * so generating from it would couple every PWA build to a local API build. These
 * are transcribed from `api/src/services/**` and verified against the spec.
 *
 * The cost is drift: the API can change a response shape and this file will
 * still compile. When an endpoint changes, diff against `api/openapi.json`.
 */

/* ---------- Auth / identity ---------------------------------------------- */

export type MemberRole = 'admin' | 'user';

/**
 * Clerk `publicMetadata`, mirrored into the session token as custom claims.
 * Written at provisioning time by `POST /master-admin/stores/:storeId/devices`.
 *
 * `store_id` and `member_id` are never sent to the API from here — every
 * endpoint reads them from the JWT. They are used client-side only, for the
 * routes that take a store or member id in the *path*.
 */
export interface MemberPublicMetadata {
  store_id: string;
  member_id: string;
  role: MemberRole;
  active: boolean;
}

export interface Member {
  id: string;
  store_id: string;
  username: string;
  role: MemberRole;
  active: boolean;
  created_at: string;
}

export interface DevicePassword {
  memberId: string;
  username: string;
  password: string;
  passwordUpdatedAt: string | null;
}

/* ---------- Catalog ------------------------------------------------------- */

/**
 * The customer grid shape. Deliberately the smaller of the two — what the PWA
 * reads becomes what the API cannot change.
 */
export interface GarmentSummary {
  garmentId: string;
  name: string;
  price: number | null;
  category: string;
  displayImageUrl: string | null;
}

/**
 * The admin management shape: the customer fields plus the two an owner's
 * screen cannot work without.
 *
 * `sourceImageUrl === null` is the whole point of G37 — such a garment looks
 * perfect in the grid and fails every try-on with `garment_unavailable`. This
 * list is the only place it can surface.
 */
export interface AdminGarmentSummary extends GarmentSummary {
  sourceImageUrl: string | null;
  displayOrder: number;
  /**
   * The ~320px WebP the admin screens should render — ~20–30KB. A reorder row
   * draws it in a 40×56 box, so the full image there is roughly 1300× more pixels
   * than are displayed.
   *
   * ℹ️ **The win is decode memory more than bytes.** `displayImageUrl` is commonly
   * only ~300–400KB (10MB hard cap on upload), but the browser holds any image as
   * `width × height × 4` bytes of RAM whatever the file weighs — a 2000×1500 photo
   * is ~12MB live — and the admin list draws 150 rows of them.
   *
   * ⚠️ **Nullable — always fall back to `displayImageUrl`.** Garments uploaded
   * before thumbnails existed have none until their display image is re-uploaded,
   * and one with only a `source` image never had a display image to derive from.
   * Use `thumbnailFor` rather than reading this directly.
   */
  thumbImageUrl: string | null;
}

/**
 * The url an admin list row should load: the thumbnail when there is one, the full
 * display image otherwise.
 *
 * A helper rather than `a ?? b` at each call site because the fallback is the part
 * that is easy to forget, and forgetting it renders nothing at all for every
 * garment uploaded before thumbnails existed.
 */
export function thumbnailFor(garment: AdminGarmentSummary): string | null {
  return garment.thumbImageUrl ?? garment.displayImageUrl;
}

export interface CatalogPage<T> {
  garments: T[];
  hasMore: boolean;
}

export interface CategoriesResponse {
  categories: string[];
}

/**
 * One entry in the client-side search index — `GET /catalog/garment-index`.
 *
 * ⚠️ **No image url, and that absence is the feature.** The whole catalogue is
 * fetched at once here, which is only safe because two text columns cannot pull
 * an image however this is rendered. A match is opened through `getGarment`, one
 * garment at a time, so search never turns into a bulk image load.
 */
export interface GarmentIndexEntry {
  garmentId: string;
  name: string;
}

export interface GarmentIndexResponse {
  garments: GarmentIndexEntry[];
}

export interface StoreCategory {
  category: string;
  prompt_template: string;
  created_at: string;
}

export interface CreateGarmentBody {
  name: string;
  category: string;
  price?: number;
}

/** `display_order` is snake_case here because the API takes it that way. */
export interface UpdateGarmentBody {
  name?: string;
  category?: string;
  price?: number;
  display_order?: number;
}

export interface ReorderEntry {
  id: string;
  display_order: number;
}

export type ImageSlot = 'display' | 'source';

/* ---------- Try-on -------------------------------------------------------- */

/** The five formats Gemini reads. `image/jpg` is accepted and normalised. */
export const SUPPORTED_PHOTO_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

export type SupportedPhotoType = (typeof SUPPORTED_PHOTO_TYPES)[number];

export interface UploadUrlResponse {
  uploadUrl: string;
  photoKey: string;
}

/**
 * `POST /try-on/adhoc-upload-url` — the same presigned PUT, for a garment
 * photographed at the counter rather than the customer standing in front of it.
 *
 * ⚠️ **This one presigns into the PUBLIC bucket**, which nothing else the PWA
 * touches does. The key is shaped `garments/{storeId}/temp/{uuid}.{ext}` and is
 * readable by anyone holding its url — already true of every catalogue garment,
 * and the reason it is acceptable here is that the subject is merchandise on a
 * counter rather than a person. The customer's own photo is untouched by this
 * and keeps its private bucket.
 *
 * ⚠️ **The bucket needs a CORS rule allowing `PUT` from this origin**, which the
 * private one has had all along and the public one did not. A missing rule
 * surfaces as `upload_unreachable` — indistinguishable from being offline, since
 * the browser does not report a CORS refusal to script.
 */
export interface AdHocUploadUrlResponse {
  uploadUrl: string;
  garmentKey: string;
}

export type GenerateReason =
  | 'quota_exceeded'
  | 'garment_unavailable'
  /**
   * ⚠️ **`POST /try-on/generate-adhoc` only.** An ad-hoc garment has no row and
   * so no category of its own — the device names one, and this is the server
   * saying that name is not a category this store has. The catalogue path
   * cannot produce it: there the category comes off the garment's row, guarded
   * by a foreign key.
   *
   * In practice it means the cached category list the picker was drawn from is
   * stale, so the fix is a refetch rather than anything the customer does.
   */
  | 'unknown_category'
  | 'already_generating'
  | 'credentials_error'
  | 'generation_failed'
  | 'generation_timeout'
  | 'configuration_error'
  | 'upstream_throttled';

export type GenerateResponse =
  | { success: true; image: string }
  | { success: false; reason: GenerateReason };

/* ---------- Quota --------------------------------------------------------- */

export interface Quota {
  store_id: string;
  monthly_allowance: number;
  pending_allowance: number | null;
  allowance_used: number;
  topup_balance: number;
  remaining: number;
  monthly_used: number;
  failed_calls: number;
  /** UTC ISO-8601 with a `Z`. Render with `formatIst`. */
  period_start: string;
  period_end: string;
}
